<template>
  <div class="domain__menu" ref="lnb">
    <h1 class="logo">
      <router-link :to="{name: 'dashboard'}">
        <i class="icon icon--logo-red"></i>
      </router-link>
    </h1>
    <p v-if="isEditMode" class="edit-mode-title">{{ $t('common.editMode') }}</p>
    <span class="domain__menu-shadow" :class="{ scrolled: !isContentHeaderScrollTop }"></span>
    <ul class="domain__list--wrap"
        :class="{ 'scrolled': !isContentHeaderScrollTop }"
        @scroll="onDomainListScroll">
      <li v-for="(item, index) in domains"
          :key="index"
          :class="{ fold: item.isExpanded, hide: item.sid !== currentSid && isEditMode }"
          class="domain-item">
        <p @click="onDomainClick(item)"
           class="domain-text"
           :title="item.domain"
           :class="{ on: item.sid === currentSid, 'edit-mode': isEditMode }">
          {{ item.domain }}
          <i class="icon icon--toggle-slide14"></i>
        </p>
        <ul class="site__list--wrap" v-if="item.sid === currentSid">
          <li v-for="(siteCode, index) in item.site_code" :key="'code' + index"
            class="site-item ellipsis"
            :class="{ fold: siteCode === currentSiteCode && isCurrentSiteCodeFold, on: siteCode === currentSiteCode }">
            <p @click="onSiteCodeClick(siteCode)"
               class="site-text"
               :title="siteCode"
               :class="{ on: siteCode === currentSiteCode, 'edit-mode': isEditMode }">
              {{ siteCode }}
              <i class="icon icon--toggle-slide14"></i>
            </p>
            <ul class="category__list--wrap" v-if="siteCode === currentSiteCode">
              <li v-for="(category, index) in domainCategories" :key="index"
                class="category-item ellipsis"
                :class="{
                  'edit-mode': isEditMode,
                  active: currentDashboardCategory.index === index &&
                  currentDashboardCategory.categoryId === category._id
                }"
                @click="onUpdateCurrentCategoryIndex(index, category._id)">
                <template v-if="category._id === '__shared__'">{{ $t('dashboard.pageShare.folderTitle') }}</template>
                <template v-else>{{ category._id }}</template>
                <i v-if="hasSharedApply(category) || category._id === '__shared__'" class="icon icon--shared"></i>
              </li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { EventBus } from '@/libs/event-bus'

export default {
  name: 'DomainList',
  data () {
    return {
      selectedItem: {},
      isCurrentSiteCodeFold: true,
      isContentHeaderScrollTop: true
    }
  },
  props: [
    'isEditMode',
    'domains',
    'currentSid',
    'currentSiteCode',
    'domainCategories'
  ],
  computed: {
    ...mapState([
      'currentDashboardCategory'
    ])
  },
  mounted () {
    EventBus.$on('scrollUpdateCurrentCategoryIndex', (index, categoryId) => {
      this.setCurrentDashboardCategory({
        categoryId,
        index
      })
    })
  },
  methods: {
    ...mapMutations([
      'setCurrentDashboardCategory'
    ]),
    hasSharedApply (category) {
      let hasSharedApply = false
      for (const apply of category.applies) {
        if (apply.shared) hasSharedApply = true
      }
      return hasSharedApply
    },
    onDomainClick (item) {
      this.isCurrentSiteCodeFold = true
      this.$emit('onDomainSelect', item)
    },
    onSiteCodeClick (item) {
      if (this.currentSiteCode === item) {
        this.isCurrentSiteCodeFold = !this.isCurrentSiteCodeFold
      } else {
        this.isCurrentSiteCodeFold = true
      }
      this.onUpdateCurrentCategoryIndex(0, item)
      this.$emit('onSiteCodeSelect', item)
    },
    onDomainListScroll (event) {
      this.isContentHeaderScrollTop = event.currentTarget.scrollTop === 0
    },
    onUpdateCurrentCategoryIndex (index, categoryId) {
      this.setCurrentDashboardCategory({
        categoryId,
        index
      })
      EventBus.$emit('updateCurrentCategoryIndex', index)
    }
  }
}
</script>

<style lang="scss" scoped>
.domain__menu {
  .edit-mode-title {
    font-size: 22px;
    font-weight: bold;
    line-height: 1;
    color: #289eef;
    position: absolute;
    top: 88px;
    left: 12px;
  }

  .domain__menu-shadow.scrolled {
    content: '';
    display: block;
    width: 210px;
    height: 12px;
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0));
    position: relative;
    top: 102px;
    left: 0;
    z-index: 10;
    pointer-events: none;
  }

  .domain__list--wrap {
    margin-top: 102px;
    height: calc(100vh - 135px); // 135 = logo's height + margin-top
    overflow-y: scroll;
    border-top: 1px solid #ebebeb;
    position: relative;
    z-index: -1;

    &.scrolled {
      top: -12px;
    }

    .domain-item {
      min-height: 50px;
      padding: 8px 9px 8px 9px;
      box-sizing: border-box;
      border-bottom: 1px solid #ebebeb;

      &.fold {
        .site__list--wrap {
          display: block;
        }
        .domain-text .icon--toggle-slide14 { transform: translateY(-50%) rotate(180deg); }
      }

      &.hide {
        display: none;
        opacity: 0;
      }

      .site__list--wrap {
        display: none;
      }

      .domain-text {
        pointer-events: inherit;
        position: relative;
        display: inline-block;
        width: 100%;
        height: 100%;
        line-height: 32px;
        padding-left: 3px;
        padding-right: 20px;
        box-sizing: border-box;
        font-size: 14px;
        font-weight: bold;
        color: #2f2f2f;
        border-radius: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;

        &.on {
          color: $brandColor;
        }
        &.on.edit-mode {
          color: #289eef;
        }

        .icon {
          position: absolute;
          top: 50%;
          right: 5px;
          transform: translateY(-50%);
        }
      }
    }
  }

  .site__list--wrap {
    .site-item {
      border: 1px solid #e7e7e7;
      border-radius: 12.5px;
      color: #444444;
      margin-top: 10px;
      width: 100%;
      font-size: 13px;
      line-height: 21px;
      padding: 1px 8px 1px 8px;
      box-sizing: border-box;
      position: relative;

      .category__list--wrap {
        display: none;
      }

      &.fold {
        border-color: #bbbbbb;
        padding-top: 8px;
        padding-bottom: 10px;

        .category__list--wrap {
          display: block;
        }
        .site-text .icon--toggle-slide14 {
          top: 18px;
          transform: translateY(-50%) rotate(180deg);
        }
      }

      .site-text {
        @include textEllipsis;
        padding: 0 25px 0 5px;
        cursor: pointer;

        &.on {
          color: $brandColor;
        }

        &.on.edit-mode {
          color: #289eef;
        }
      }

      .icon {
        position: absolute;
        top: 50%;
        right: 7px;
        transform: translateY(-50%);
      }
    }
  }

  .category__list--wrap {
    .category-item {
      color: #444444;
      margin-top: 9px;
      width: 100%;
      height: 21px;
      border-radius: 10px;
      font-size: 13px;
      line-height: 21px;
      padding-left: 11px;
      padding-right: 20px;
      box-sizing: border-box;
      cursor: pointer;
      position: relative;

      &:not(.active):hover {
        background-color: #ebebeb;
      }
      &.active {
        background-color: $brandColor;
        color: #fff;

        .icon {
          background-image: url(../../assets/img/dashboard/icon_shared_hover.svg);
        }
      }
      &.active.edit-mode {
        background-color: #289eef;
      }

      .icon {
        position: absolute;
        top: 50%;
        right: 7px;
        transform: translateY(-50%);
      }
    }
  }
}
</style>
